import React from "react";
import { CheckCircle2, Wrench, Clock, Euro } from "lucide-react";
import PageHeader from "../components/PageHeader";
import CTABanner from "../components/CTABanner";
import Tarieven from "../components/Tarieven";
import ITSupportTeaser from "../components/ITSupportTeaser";
import { images } from "../mock";

const reparaties = [
  { title: "Laptop scherm vervanging", price: "vanaf €89", time: "Binnen 1 dag" },
  { title: "Toetsenbord reparatie", price: "vanaf €45", time: "Zelfde dag" },
  { title: "Accu vervanging", price: "vanaf €69", time: "Zelfde dag" },
  { title: "SSD upgrade + kloon", price: "vanaf €79", time: "Binnen 1 dag" },
  { title: "Reiniging en koeling", price: "vanaf €55", time: "Zelfde dag" },
  { title: "Virus & malware verwijdering", price: "vanaf €65", time: "Zelfde dag" },
  { title: "Windows herinstallatie", price: "vanaf €75", time: "Binnen 1 dag" },
  { title: "Data herstel", price: "vanaf €95", time: "2–5 dagen" },
];

const process = [
  {
    step: "01",
    title: "Aanmelden",
    description: "Bel of vul het contactformulier in. Wij luisteren naar uw probleem.",
  },
  {
    step: "02",
    title: "Diagnose",
    description:
      "Gratis en vrijblijvende diagnose. U ontvangt vooraf een duidelijke prijsopgave.",
  },
  {
    step: "03",
    title: "Reparatie",
    description: "Snelle en vakkundige uitvoering, met originele onderdelen waar mogelijk.",
  },
  {
    step: "04",
    title: "Garantie",
    description:
      "3 maanden garantie op alle reparaties. Tevredenheid staat bij ons op nummer één.",
  },
];

const ComputerReparaties = () => {
  return (
    <>
      <PageHeader
        eyebrow="— Computer reparaties"
        title="Vakkundige reparaties, eerlijke prijzen"
        subtitle="Van een kapot scherm tot een niet-opstartende PC — wij repareren alle merken en modellen. Altijd met vooraf duidelijke prijsopgave."
      />

      {/* Info section */}
      <section className="py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <img
              src={images.circuitBoard}
              alt="Computer reparatie"
              loading="lazy" className="w-full h-[500px] object-cover rounded"
            />
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-5 leading-tight">
                Waarom uw reparatie bij Computheer?
              </h2>
              <p className="text-neutral-600 leading-relaxed mb-8">
                Onze ervaren technici werken dagelijks aan uiteenlopende reparaties. Wij
                onderzoeken elk probleem grondig en adviseren eerlijk over de beste oplossing,
                zonder onnodige kosten.
              </p>
              <ul className="space-y-3">
                {[
                  "Gratis diagnose & vrijblijvende prijsopgave",
                  "3 maanden garantie op alle reparaties",
                  "Alle merken: Apple, HP, Dell, Lenovo, ASUS, Acer",
                  "Gebruik van originele en kwalitatieve onderdelen",
                  "Vakkundige werkplaats met moderne apparatuur",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-neutral-800">
                    <CheckCircle2 size={20} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Tarieven />

      <ITSupportTeaser variant="reparaties" />

      {/* Price list */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10">
          <div className="mb-12">
            <p className="text-sm tracking-[0.3em] uppercase text-neutral-500 mb-3">— Tarieven</p>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900">
              Indicatieve prijslijst
            </h2>
            <p className="text-neutral-600 mt-3 max-w-xl">
              Onderstaande prijzen zijn richtlijnen. U ontvangt altijd vooraf een duidelijke
              prijsopgave.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-neutral-200 bg-white">
            {reparaties.map((r, i) => (
              <div
                key={i}
                className="p-6 border-r border-b border-neutral-200 flex items-center justify-between gap-4 hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-neutral-900 text-white flex items-center justify-center flex-shrink-0">
                    <Wrench size={16} />
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-900">{r.title}</p>
                    <p className="text-sm text-neutral-500 flex items-center gap-1 mt-0.5">
                      <Clock size={12} /> {r.time}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-neutral-500 uppercase tracking-wider">Prijs</p>
                  <p className="font-bold text-neutral-900 flex items-center gap-1 justify-end">
                    <Euro size={14} />
                    {r.price.replace("€", "")}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-sm tracking-[0.3em] uppercase text-neutral-500 mb-3">
              — Zo werken wij
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900">
              Een simpele, transparante werkwijze
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-neutral-200">
            {process.map((p, i) => (
              <div
                key={i}
                className="p-8 border-r border-b border-neutral-200 bg-white relative overflow-hidden group"
              >
                <span className="text-7xl font-bold text-neutral-100 absolute top-2 right-3 group-hover:text-neutral-200 transition-colors">
                  {p.step}
                </span>
                <div className="relative">
                  <p className="text-sm text-neutral-500 mb-2">Stap {p.step}</p>
                  <h3 className="text-xl font-bold text-neutral-900 mb-3">{p.title}</h3>
                  <p className="text-neutral-600 leading-relaxed text-sm">{p.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
};

export default ComputerReparaties;
